import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Grid3x3, UserCheck } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { QuietEmpty, WorkerChip } from '../shared';
import { SignoffDialog } from '../SignoffDialog';
import {
  matrixKey,
  SIGNOFF_STATE_META,
  signoffState,
  useStationInstructionHashes,
  useWorkstationSignoffs,
} from '@/features/workMode/signoffs';
import { formatDateOnly } from '@/lib/formatters';
import type { WorkLookups } from '@/features/workMode/useLookups';
import type { FloorWorkstation, WorkstationSignoff } from '@/features/workMode/types';

interface StationPeopleTabProps {
  station: FloorWorkstation;
  lookups: WorkLookups;
  canManage: boolean;
}

/** Who is signed off to run this bench, straight from the employee matrix. */
export function StationPeopleTab({ station, lookups, canManage }: StationPeopleTabProps) {
  const { data: signoffs = [], isLoading } = useWorkstationSignoffs();
  const { data: hashes } = useStationInstructionHashes();
  const [signingOff, setSigningOff] = useState(false);

  const { current, active } = useMemo(() => {
    const active = new Map<string, WorkstationSignoff>();
    for (const s of signoffs) {
      if (!s.revoked_at && s.workstation_id) active.set(matrixKey(s.workstation_id, s.user_id), s);
    }
    const current = signoffs
      .filter((s) => s.workstation_id === station.id && !s.revoked_at)
      .map((s) => ({
        signoff: s,
        person: lookups.person(s.user_id),
        state: signoffState(s, hashes),
      }))
      .filter((r) => r.person.isActive)
      .sort((a, b) => a.person.name.localeCompare(b.person.name));
    return { current, active };
  }, [signoffs, station.id, lookups, hashes]);

  const changed = current.filter((r) => r.state === 'changed').length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {current.length === 0
            ? 'Nobody is signed off on this station yet.'
            : `${current.length} ${current.length === 1 ? 'person is' : 'people are'} signed off here.`}
          {changed > 0 && (
            <span className="text-fresh-salmon-dark">
              {' '}
              {changed} need a fresh sign-off — the instructions changed.
            </span>
          )}
        </p>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" size="sm">
            <Link to="/mfg/production/employee-matrix">
              <Grid3x3 className="h-4 w-4" />
              Employee matrix
            </Link>
          </Button>
          {canManage && (
            <Button size="sm" onClick={() => setSigningOff(true)}>
              <UserCheck className="h-4 w-4" />
              Sign someone off
            </Button>
          )}
        </div>
      </div>

      {isLoading ? null : current.length === 0 ? (
        <QuietEmpty>No active sign-offs.</QuietEmpty>
      ) : (
        <ul className="divide-y divide-border rounded-2xl border border-border bg-card">
          {current.map(({ signoff, person, state }) => (
            <li key={signoff.id} className="flex items-center gap-3 px-4 py-3">
              <WorkerChip
                person={person}
                sub={`Signed off ${formatDateOnly(signoff.signed_off_on)}${signoff.signed_off_by ? ` by ${lookups.personName(signoff.signed_off_by)}` : ''}`}
                size={36}
                className="flex-1"
              />
              <Badge variant={SIGNOFF_STATE_META[state].variant}>
                {SIGNOFF_STATE_META[state].label}
              </Badge>
            </li>
          ))}
        </ul>
      )}

      {signingOff && (
        <SignoffDialog
          stations={lookups.stationList.filter((s) => s.is_active || s.id === station.id)}
          people={lookups.peopleList.filter((p) => p.isActive && p.role !== 'external_lender')}
          active={active}
          initialStationId={station.id}
          onClose={() => setSigningOff(false)}
        />
      )}
    </div>
  );
}
